import { useState } from "react";
import { FolderPlusIcon, EnvelopeIcon } from "@heroicons/react/24/outline";
import { SettingsPanel, Button, Avatar, Spinner, Badge } from "../../shared";
import UserStatsBar from "./UserStatsBar";
import UserAddModal from "./UserAddModal";
import UserExistsModal from "./UserExistsModal";
import UserActivateModal from "./UserActivateModal";
import UserDeleteModal from "./UserDeleteModal";
import useForm from "../../hooks/useForm";
import { Link } from "react-router-dom";
import {
  useUpdateUserMutation,
  useAddUserMutation,
  useDeleteUserMutation,
} from "../../redux/services/users/usersApi";
import { toast } from "react-toastify";
import { sendEmail } from "../../redux/reducers/users/userSlice";
import { useDispatch } from "react-redux";
import { useAuth } from "../../hooks/useAuth";
import ButtonDropdown from "../../shared/ButtonDropdown";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../constants/access-control";
import {
  formatUserDate,
  getUserPlatformLabel,
  getUserStatusMeta,
  getUserTenantRoleNames,
} from "../../pages/Settings/Users/user.utils";
import { normalizeAccountType } from "../../constants/select";

const UsersSettingsLayout = ({
  data = {},
  stats = {},
  statusFilter = "all",
  onStatusFilterChange,
  title = "Users",
  subtitle = "Review team members, their roles, and account status.",
  deactivate,
  addData,
  icon = (
    <FolderPlusIcon className="w-12 h-12 mx-auto text-gray-400 stroke-1" />
  ),
  ...props
}) => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canManageUsers = hasPermission(PERMISSIONS.SETTINGS_USERS_MANAGE);
  const formHook = useForm();
  const dispatch = useDispatch();

  const [addUser, { isLoading: addingUser }] = useAddUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser] = useDeleteUserMutation();

  const [addModalVisible, setAddModalVisible] = useState(false);
  const [existsModal, setExistsModal] = useState(false);
  const [activateModal, setActivateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [activateModalType, setActivateModalType] = useState("deactivate");
  const [itemToActivate, setItemToActivate] = useState("");
  const [itemToDelete, setItemToDelete] = useState("");
  const [loading, setLoading] = useState(false);

  const dataKeys = Object.keys(data);
  const dataEmpty = dataKeys.length > 0;
  const isLoading = loading || props.loading;
  const emptyData = {
    content: isLoading ? (
      <div className="p-5">
        <Spinner />
      </div>
    ) : (
      <div className="py-5">
        <div className="text-center">
          {icon}
          <h3 className="mt-2 font-semibold text-sm">No Users found</h3>
          <p className="text-sm text-gray-500">
            Please try a different search term
          </p>
          {/* <div className="mt-3">{renderButton("xs", "Invite User")}</div> */}
        </div>
      </div>
    ),
  };

  const dataMap = data.users || [];

  function renderButton(size, content) {
    const wh = size === "md" ? 5 : 4;
    return (
      <Button
        onClick={() => setAddModalVisible(true)}
        size={size || "xs"}
        icon={<EnvelopeIcon className={`w-${wh} h-${wh} mr-2`} />}
      >
        {content || "Add data"}
      </Button>
    );
  }

  async function email(userId, type, options) {
    const email = await dispatch(
      sendEmail({ userId, type, ...options })
    ).unwrap();

    toast(email.message, {
      type: email.status,
      toastId: "user-email-sent-" + userId,
    });
  }

  function handleActivateModal(key, modalType) {
    setActivateModalType(modalType);
    setActivateModal(true);
    setItemToActivate(key);
  }

  function handleActivate() {
    if (activateModalType === "activate") {
      email(itemToActivate, "activate", {
        body: "You are receiving this email to reactivate your account.",
      });
    } else {
      updateUser({ id: itemToActivate, data: { active: false } });
    }
    closeActivateModal();
  }

  function closeActivateModal() {
    setLoading(false);
    setActivateModal(false);
    setItemToActivate("");
  }

  async function handleClickAddUser() {
    const role = normalizeAccountType(formHook.form.role);

    try {
      const res = await addUser({
        ...formHook.form,
        role,
        type: role,
      }).unwrap();

      toast(res.message, {
        toastId: "settings-add-user",
        type: res.status,
      });

      setAddModalVisible(false);
      formHook.clearForm();
    } catch (error) {
      toast.error(error?.data?.message || "Unable to invite user.", {
        toastId: "settings-add-user-error",
      });
    }
  }

  function handleAddRate() {
    setLoading(true);
    addData({ ...formHook.form, configType: "rates" });
    setAddModalVisible(false);
    setExistsModal(false);
    formHook.clearForm();
    setLoading(false);
  }

  function handleDeleteUserModal(userId) {
    setDeleteModal(true);
    setItemToDelete(userId);
  }

  async function handleDeleteUser() {
    const res = await deleteUser(itemToDelete).unwrap();

    toast(res.message, {
      toastId: "settings-delete-user",
      type: res.status,
    });

    if (res.status === "success") {
      setDeleteModal(false);
    }
  }

  function itemLayout(item, i) {
    const isSelf = item.email === user.email;
    const assignedTenantRoles = getUserTenantRoleNames(item);
    const status = getUserStatusMeta(item);
    const platformLabel = getUserPlatformLabel(item);

    const actions = [
      {
        id: "activate",
        title: item.active ? "Deactivate" : "Send Activation Link",
        remove: isSelf || !canManageUsers,
        onClick: () =>
          item.active
            ? handleActivateModal(item._id, "deactivate")
            : handleActivateModal(item._id, "activate"),
      },
      {
        id: "delete",
        remove: !canManageUsers,
        title: "Delete",
        onClick: () => handleDeleteUserModal(item._id),
      },
    ].filter((i) => {
      if (i.remove) {
        return false;
      }
      return true;
    });
    const isLoading = loading || props.loading;
    return (
      <div
        key={i}
        className="grid grid-cols-12 items-center gap-3 border-t border-gray-100 bg-white p-4 first:border-t-0"
      >
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="flex items-center col-span-12 md:col-span-5">
              <Avatar size="sm" src={item.avatar} name={item.fullName} />
              <div className="ml-3 min-w-0">
                <Link
                  to={`/settings/users/${item._id}`}
                  className="block truncate text-base font-medium text-primary-600 hover:text-primary-700 hover:underline"
                >
                  {item.fullName || "Unnamed user"}
                </Link>
                <div className="truncate text-sm text-gray-500">{item.email}</div>
              </div>
            </div>
            <div className="col-span-6 text-sm text-gray-800 md:col-span-2">{platformLabel}</div>
            <div className="col-span-6 md:col-span-3">
              {assignedTenantRoles.length > 0 ? (
                <div className="text-sm text-gray-700">{assignedTenantRoles.join(", ")}</div>
              ) : (
                <Badge color="amber" label="No roles" />
              )}
            </div>
            <div className="col-span-6 md:col-span-1">
              <Badge color={status.color} label={status.label} />
            </div>
            <div className="col-span-6 text-sm text-gray-500 md:col-span-1">
              {formatUserDate(item.dateCreated || item.createdAt)}
            </div>
            <div className="col-span-12 flex justify-end md:col-span-1">
              <ButtonDropdown size="xs" type="light" data={actions}>
                Actions
              </ButtonDropdown>
              {/* <div
                className="text-xs text-red-500 cursor-pointer"
                onClick={() => handleDeleteUserModal(item._id)}
              >
                <TrashIcon className="w-5 stroke-red-500" />
              </div> */}
              {/* <Button
                disabled={isSelf}
                type="alternative"
                onClick={() =>
                  item.active
                    ? handleActivateModal(item._id, "deactivate")
                    : handleActivateModal(item._id, "activate")
                }
                style={{ minWidth: 170 }}
              >
                {item.active ? "Deactivate" : "Send Activation Link"}
              </Button> */}
            </div>
          </>
        )}
      </div>
    );
  }

  const actions = renderButton("xs", "Invite User");

  const isEmpty = dataMap.length === 0;
  const settingData = isEmpty ? [] : dataMap;

  return (
    <>
      <div className="space-y-4">
        <UserStatsBar
          stats={stats}
          statusFilter={statusFilter}
          onStatusFilterChange={onStatusFilterChange}
        />
        <SettingsPanel
          itemLayout={itemLayout}
          title={title}
          empty={emptyData}
          subtitle={subtitle}
          data={settingData}
          actions={actions}
        />
      </div>
      <UserAddModal
        form={formHook}
        visible={addModalVisible}
        setVisible={setAddModalVisible}
        submit={handleClickAddUser}
        loading={loading || addingUser}
      />
      <UserExistsModal
        visible={existsModal}
        setVisible={setExistsModal}
        submit={handleAddRate}
        loading={loading}
      />
      <UserActivateModal
        visible={activateModal}
        setVisible={setActivateModal}
        submit={handleActivate}
        loading={loading}
        type={activateModalType}
      />
      <UserDeleteModal
        visible={deleteModal}
        setVisible={setDeleteModal}
        submit={handleDeleteUser}
        loading={loading}
      />
    </>
  );
};

export default UsersSettingsLayout;
